import { registerView } from '../viewRegistry';
import { interactiveRegistry, navigateTo } from '../interactivity';
import { getCanonicalSafeArea } from '../safeArea';
import { renderBlobBackground } from '../renderer/plasma';
import { DopeTextRenderer } from '../renderer/bitmapFont';
import { renderSeal } from '../renderer/seal';
import useCoreSession, { coreErrorMessage, formatMinor } from '../../stores/coreSession';
import { solvencyLabel } from '../../utils/solvency';
import DopeUI, { TERMINAL } from '../ui/DopeOS-UI';

const state = { runs: [], loaded: false, loading: false, selected: 0, error: null };

function text(ctx, value, x, y, color = TERMINAL.TEXT) {
  ctx.fillStyle = color;
  DopeTextRenderer.renderDOSText(ctx, value, x, y, 1);
}

function clip(value, max = 24) {
  const textValue = String(value ?? '--');
  return textValue.length > max ? `${textValue.slice(0, max - 1)}…` : textValue;
}

function rule(ctx, x, y, w, color = TERMINAL.GREEN_DARK) {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, w, 1);
}

async function loadRuns() {
  if (state.loading) return;
  state.loading = true;
  state.error = null;
  try {
    state.runs = await useCoreSession.getState().loadRuns(50);
    state.loaded = true;
    if (state.selected >= state.runs.length) state.selected = 0;
  } catch (error) {
    state.error = coreErrorMessage(error);
  } finally {
    state.loading = false;
  }
}

function drawTop(ctx, width) {
  const session = useCoreSession.getState();
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, width, 26);
  rule(ctx, 0, 25, width);
  text(ctx, 'DOPEOS // ARCHIVE', 8, 5, TERMINAL.TEXT);
  if (width >= 500) text(ctx, 'RUN HISTORY // READ ONLY', 232, 5, TERMINAL.TEXT_DIM);
  text(ctx, session.status === 'online' ? 'LIVE' : 'OFFLINE', width - 64, 5, session.status === 'online' ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
}

function drawList(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'RUN HISTORY');
  if (!state.runs.length) {
    text(ctx, state.error || (state.loading ? 'SYNCING...' : 'NO RUNS RECORDED.'), x + 12, y + 30, state.error ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT_DIM);
    return;
  }
  state.runs.forEach((run, index) => {
    const rowY = y + 30 + index * 28;
    if (rowY + 22 > y + h - 10) return;
    const selected = index === state.selected;
    const label = run.result ? 'SEALED' : String(run.status || '--').toUpperCase();
    if (selected) {
      ctx.fillStyle = TERMINAL.GREEN_DARK;
      ctx.fillRect(x + 6, rowY - 4, w - 12, 24);
    }
    text(ctx, `${selected ? '>' : ' '} ${run.sessionId?.slice(-8) || '--'}`, x + 12, rowY, selected ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    text(ctx, `D${run.day ?? '--'} ${label}`, x + 116, rowY, run.result ? TERMINAL.GREEN : TERMINAL.TEXT_DIM);
    interactiveRegistry.register({
      id: `archive-run-${run.sessionId}`,
      x: x + 6,
      y: rowY - 4,
      w: w - 12,
      h: 24,
      onClick: () => { state.selected = index; },
    });
  });
}

function drawDetail(ctx, x, y, w, h) {
  DopeUI.Panel(ctx, x, y, w, h, 'RUN RECORD');
  const run = state.runs[state.selected];
  if (!run) {
    text(ctx, 'SELECT A RUN.', x + 12, y + 30, TERMINAL.TEXT_DIM);
    return;
  }
  const result = run.result;
  if (!result) {
    const rows = [
      ['RUN', run.sessionId || '--'],
      ['STATUS', String(run.status || '--').toUpperCase()],
      ['STARTED', run.startedAt || '--'],
      ['DAY', `${run.day ?? '--'}/30`],
      ['SIGNATURE', '--'],
      ['ARCHIVE PTS', '--'],
    ];
    let cursor = y + 30;
    rows.forEach(([label, value]) => {
      text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
      text(ctx, String(value).slice(0, 30), x + 104, cursor, TERMINAL.TEXT);
      cursor += 18;
    });
    rule(ctx, x + 12, cursor + 2, w - 24);
    text(ctx, 'UNSEALED // RUN IN PROGRESS OR LEGACY.', x + 12, cursor + 16, TERMINAL.TEXT_DIM);
    text(ctx, 'READ-ONLY. NO REWRITE.', x + 12, cursor + 34, TERMINAL.GREEN);
    return;
  }

  const rows = [
    ['RUN', clip(result.runId)],
    ['STATUS', 'SEALED'],
    ['DAY', result.day],
    ['SEALED', clip(result.sealedAt)],
    ['CASH', formatMinor(result.cash)],
    ['DEBT', formatMinor(result.debt)],
    ['INVENTORY', formatMinor(result.inventoryValue)],
    ['NET WORTH', formatMinor(result.netWorth)],
    ['HRONIR', clip(`${result.hronirStatus} / ${result.hronirOutcome}`, 24)],
    ['ARCHIVE Δ', result.archivePointsDelta],
    ['ARCHIVE TOTAL', result.archivePointsLifetime],
    ['VERDICT', solvencyLabel(result)],
  ];
  let cursor = y + 28;
  rows.forEach(([label, value]) => {
    text(ctx, label, x + 12, cursor, TERMINAL.TEXT_DIM);
    text(ctx, String(value), x + 120, cursor, label === 'NET WORTH' ? TERMINAL.TEXT_BRIGHT : TERMINAL.TEXT);
    cursor += 15;
  });
  rule(ctx, x + 12, cursor + 2, w - 24);

  // L6.SEAL.1 — deterministic archive seal from the backend visualSeed.
  if (result.visualSeed) {
    renderSeal(ctx, result.visualSeed, x + 12, cursor + 14, w - 24, 48, TERMINAL.GREEN);
    return;
  }
  text(ctx, String(result.signature).slice(0, 32), x + 12, cursor + 16, TERMINAL.TEXT_DIM);
  text(ctx, String(result.signature).slice(32), x + 12, cursor + 30, TERMINAL.TEXT_DIM);
}

registerView('archive', function renderArchive(ctx, time, width, height) {
  interactiveRegistry.clear();
  const safe = getCanonicalSafeArea({ w: width || 640, h: height || 400 });
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, 0, safe.width, safe.height);
  ctx.save();
  ctx.globalAlpha = 0.12;
  renderBlobBackground(ctx, time, safe.width, safe.height);
  ctx.restore();

  if (!state.loaded && !state.loading && useCoreSession.getState().status === 'online') loadRuns();

  drawTop(ctx, safe.width);
  const mobile = safe.width < 500;
  if (mobile) {
    drawList(ctx, 8, 32, safe.width - 16, 220);
    drawDetail(ctx, 8, 260, safe.width - 16, 270);
  } else {
    drawList(ctx, 8, 36, 248, 280);
    drawDetail(ctx, 264, 36, 368, 280);
  }

  const footerY = mobile ? 542 : 328;
  ctx.fillStyle = TERMINAL.BLACK;
  ctx.fillRect(0, footerY, safe.width, safe.height - footerY);
  rule(ctx, 0, footerY, safe.width);
  text(ctx, 'TAB FOCUS   ENTER SELECT   ESC DESKTOP', 8, mobile ? footerY + 8 : 360, TERMINAL.TEXT_DIM);
  const backX = mobile ? 8 : safe.width - 200;
  const backY = mobile ? 600 : 336;
  const backW = mobile ? safe.width - 16 : 192;
  DopeUI.Button(ctx, backX, backY, backW, 24, 'BACK TO DESKTOP', 'normal');
  interactiveRegistry.register({
    id: 'archive-back', x: backX, y: backY, w: backW, h: 24,
    onClick: () => navigateTo('dopeos_home'),
  });
  if (!mobile) {
    DopeUI.Button(ctx, 8, 336, 100, 24, state.loading ? 'SYNC' : 'REFRESH', state.loading ? 'disabled' : 'normal');
    interactiveRegistry.register({
      id: 'archive-refresh', x: 8, y: 336, w: 100, h: 24,
      onClick: () => { state.loaded = false; loadRuns(); },
    });
    text(ctx, 'ARCHIVE // READ ONLY. NO REWRITE.', 120, 384, TERMINAL.GREEN);
  }
});
